import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  avaliacao as api,
  type NotaDaAvaliacao,
  type TurnoAvaliavel,
  type TurnosDaConversa,
} from '../api/avaliacao'
import { formatar } from '../i18n'
import './avaliacao.css'

/**
 * O polegar e a frase sobre **um turno** do assistente (demanda 209).
 *
 * ## 🔴 Por que por turno, e não pela conversa
 *
 * Em 08/09 a queixa foi *"as conversas não estão boas"*, e a reunião do dia
 * seguinte começou pela pergunta sem resposta: **qual conversa, e o quê?**. Um
 * polegar na conversa inteira devolveria *"ruim"* — que é exatamente o dado que
 * já existia e não resolvia nada.
 *
 * ⭐ **Todo defeito que este projeto encontrou é de um turno**: o campo que veio
 * preenchido sem o cliente ter dito, a ferramenta que não foi chamada, a frase
 * que prometeu um retorno impossível. A âncora é a mensagem.
 *
 * ## ⚠️ A pergunta do campo não é "comentário"
 *
 * É **"o que deveria ter acontecido?"**. A diferença não é de rótulo: *comentário*
 * convida a *"ficou ruim"*, e a pergunta convida a *"ela devia ter perguntado a
 * gramatura antes de dar o preço"* — que é a frase que vira ficha.
 */
export function AvaliacaoDoTurno({ conversaExterna }: { conversaExterna: number }) {
  const { t } = useTranslation()

  const [dados, setDados] = useState<TurnosDaConversa | null>()
  const [falhou, setFalhou] = useState(false)
  const [aberto, setAberto] = useState<number | null>(null)

  useEffect(() => {
    setDados(undefined)
    setFalhou(false)

    api
      .turnos(conversaExterna)
      .then(setDados)
      .catch(() => setFalhou(true))
  }, [conversaExterna])

  // 🔴 **Falha aqui não pode derrubar o painel.** O contexto é o que o atendente
  // veio buscar; a avaliação é um extra. Some em silêncio, como a linha do tempo.
  if (falhou) return null

  if (dados === undefined)
    return (
      <section className="avaliacao">
        <h2 className="contexto__secao-titulo">{t('avaliacao.titulo')}</h2>
        <p className="contexto__vazio">{t('comum.carregando')}</p>
      </section>
    )

  if (dados === null || dados.turnos.length === 0) return null

  /** Grava e devolve a lista com a linha alterada — sem recarregar tudo. */
  async function registrar(mensagemId: number, nota: NotaDaAvaliacao, comentario: string | null) {
    await api.registrar(conversaExterna, mensagemId, nota, comentario)

    setDados((atual) =>
      atual
        ? {
            ...atual,
            turnos: atual.turnos.map((turno) =>
              turno.mensagemId === mensagemId
                ? { ...turno, minhaNota: nota, meuComentario: comentario }
                : turno,
            ),
          }
        : atual,
    )
  }

  return (
    <section className="avaliacao">
      <h2 className="contexto__secao-titulo">{t('avaliacao.titulo')}</h2>
      <p className="avaliacao__dica">{t('avaliacao.dica')}</p>

      <ul className="avaliacao__lista">
        {dados.turnos.map((turno) => (
          <Turno
            key={turno.mensagemId}
            turno={turno}
            aberto={aberto === turno.mensagemId}
            abrir={() => setAberto(aberto === turno.mensagemId ? null : turno.mensagemId)}
            registrar={registrar}
          />
        ))}
      </ul>
    </section>
  )
}

function Turno({
  turno,
  aberto,
  abrir,
  registrar,
}: {
  turno: TurnoAvaliavel
  aberto: boolean
  abrir: () => void
  registrar: (mensagemId: number, nota: NotaDaAvaliacao, comentario: string | null) => Promise<void>
}) {
  const { t } = useTranslation()

  const [texto, setTexto] = useState(turno.meuComentario ?? '')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState(false)

  async function marcar(nota: NotaDaAvaliacao) {
    setSalvando(true)
    setErro(false)

    try {
      await registrar(turno.mensagemId, nota, texto.trim() || null)

      // ⭐ O polegar para baixo **abre a caixa**: é aí que está o valor, e pedir
      // um segundo clique para escrever é onde a maioria desiste.
      if (nota === 'Ruim' && !aberto) abrir()
    } catch {
      setErro(true)
    } finally {
      setSalvando(false)
    }
  }

  async function salvarTexto() {
    if (!turno.minhaNota) return

    setSalvando(true)
    setErro(false)

    try {
      await registrar(turno.mensagemId, turno.minhaNota, texto.trim() || null)
      abrir()
    } catch {
      setErro(true)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <li className={`avaliacao__turno${turno.minhaNota ? ` avaliacao__turno--${turno.minhaNota.toLowerCase()}` : ''}`}>
      <p className="avaliacao__hora">
        {formatar.dataHora(turno.em)}
        {/* 209 §8 (25/09): a nota interna continua avaliável — o robô e a transferência também erram —,
            mas quem avalia precisa saber que aquilo o cliente nunca leu. */}
        {turno.origem === 'Sistema' && <span className="avaliacao__interna"> · {t('avaliacao.notaInterna')}</span>}
      </p>
      <p className="avaliacao__trecho">{turno.trecho}</p>

      <div className="avaliacao__acoes">
        {/* ⚠️ `aria-pressed` e não só a cor: estado que só existe em cor não
            chega a quem usa leitor de tela nem a quem não distingue as duas
            (UI-09). O rótulo diz o que o botão faz, não o que ele parece. */}
        <button
          type="button"
          className="avaliacao__polegar"
          aria-pressed={turno.minhaNota === 'Boa'}
          disabled={salvando}
          onClick={() => marcar('Boa')}
        >
          <span aria-hidden="true">👍</span> {t('avaliacao.boa')}
        </button>

        <button
          type="button"
          className="avaliacao__polegar"
          aria-pressed={turno.minhaNota === 'Ruim'}
          disabled={salvando}
          onClick={() => marcar('Ruim')}
        >
          <span aria-hidden="true">👎</span> {t('avaliacao.ruim')}
        </button>

        {turno.minhaNota && !aberto && (
          <button type="button" className="avaliacao__link" onClick={abrir}>
            {turno.meuComentario ? t('avaliacao.editarNota') : t('avaliacao.escrever')}
          </button>
        )}
      </div>

      {turno.meuComentario && !aberto && (
        <p className="avaliacao__comentario">{turno.meuComentario}</p>
      )}

      {aberto && (
        <div className="avaliacao__caixa">
          <label className="avaliacao__rotulo" htmlFor={`comentario-${turno.mensagemId}`}>
            {t('avaliacao.pergunta')}
          </label>

          <textarea
            id={`comentario-${turno.mensagemId}`}
            className="avaliacao__campo"
            rows={3}
            maxLength={2000}
            value={texto}
            placeholder={t('avaliacao.exemplo')}
            onChange={(e) => setTexto(e.target.value)}
          />

          <div className="avaliacao__acoes">
            <button
              type="button"
              className="avaliacao__salvar"
              disabled={salvando || !turno.minhaNota}
              onClick={salvarTexto}
            >
              {t('comum.salvar')}
            </button>

            {/* Sem nota não há o que comentar: a frase precisa de um polegar
                para significar alguma coisa na listagem. */}
            {!turno.minhaNota && <span className="avaliacao__dica">{t('avaliacao.primeiroONota')}</span>}
          </div>
        </div>
      )}

      {erro && <p className="avaliacao__erro">{t('avaliacao.falhou')}</p>}
    </li>
  )
}
